import React, { useState, useEffect, useRef } from "react";
import { Autocomplete, TextField, CircularProgress, Typography, Box } from "@mui/material";
import { listCars } from "../api/list-cars";
import { Car, isValidCar } from "../";

type CarAutocompleteProps = {
  car: Car;
  onCarSelected: (car: Car) => void;
  costumerId?: string;
};

const getOptionLabel = (car: Car | null): string => {
  return car && isValidCar(car)
    ? `${car.brand}, ${car.model}, (${car.plate}, ${car.chassisNumber}) |  ${car.color} - ${car.insuranceName}`
    : "";
};

export default function CarAutocomplete({ car, onCarSelected, costumerId }: CarAutocompleteProps) {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedCar, setSelectedCar] = useState<Car | null>(null);
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const limit = 100;
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (car && isValidCar(car)) {
      setSelectedCar(car);
      setInputValue(getOptionLabel(car));
    } else {
      setSelectedCar(null);
      setInputValue("");
    }
  }, [car]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 600);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  const fetchCars = async (term: string, newOffset = 0, append = false, costumerId = "") => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);

    try {
      const {
        cars,
        total: totalResults,
        error
      } = await listCars({
        plate: term,
        chassi: term,
        limit,
        offset: newOffset,
        costumerId: costumerId
      });

      const carsWithDates: Car[] = cars.map((car: any) => ({
        ...car,
        chassisNumber: car.chassis_number,
        insuranceName: car.insurance_name,
        createdAt: car.createdAt ? new Date(car.createdAt) : new Date(),
        updatedAt: car.updatedAt ? new Date(car.updatedAt) : new Date()
      }));

      if (!controller.signal.aborted) {
        if (error) {
          console.error("Error fetching cars:", error);
        } else {
          setOptions((prev) => (append ? [...prev, ...carsWithDates] : carsWithDates));
          setOffset(newOffset + limit);
          setTotal(totalResults);
        }
      }
    } catch (err) {
      if ((err as any).name !== "AbortError") {
        console.error("Fetch failed:", err);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const selectedCarText = getOptionLabel(selectedCar);
    if (selectedCarText !== "" && debouncedInputValue === selectedCarText) return;
    fetchCars(debouncedInputValue, 0, false, costumerId);
  }, [debouncedInputValue]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLUListElement;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 && !loading && options.length < total) {
      fetchCars(inputValue, offset, true, costumerId);
    }
  };

  return (
    <Autocomplete
      getOptionLabel={(option) => getOptionLabel(option as Car)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      value={selectedCar}
      onChange={(_, value) => {
        if (!value || !isValidCar(value)) {
          setSelectedCar(null);
          return;
        }
        setSelectedCar(value);
        onCarSelected(value);
      }}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        setInputValue(value);
        if (reason === "input") {
          setOffset(0);
          setOptions([]);
          setTotal(0);
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return false;
        return option.id === value.id;
      }}
      ListboxProps={{
        onScroll: handleScroll,
        style: { maxHeight: "300px", overflow: "auto" }
      }}
      noOptionsText="Nenhum veículo encontrado"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Selecione um veículo"
          onFocus={() => {
            if (inputValue.trim() === "") {
              setOffset(0);
              fetchCars("", 0, false, costumerId);
            }
          }}
          onBlur={() => {
            if (inputValue.trim() === "") {
              setOptions([]);
              setOffset(0);
              setTotal(0);
            }
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
          variant="standard"
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Typography variant="body2">
            {option.brand}, {option.model}, ({option.plate}, {option.chassisNumber}) | {option.color} | {option.insuranceName}
          </Typography>
        </Box>
      )}
    />
  );
}
